<template>
  <div>
    <b-card>
      <span class="font-weight-bolder text-primary">{{ $t('customer') }}</span>
      <!-- Button: See uploaded document -->
      <div class="mt-1">
        <b-button
          variant="info"
          class="mb-75"
          block
          @click="
            $router.push({
              name: 'clientQuoteMetadata',
              params: { id: quote.id },
            })
          "
        >
          <feather-icon
            icon="EyeIcon"
            class="mr-1"
          />
          {{ $t('breakdown') }}
        </b-button>
        <b-button
          v-if="!quoteHasPaymentActivity"
          :variant="paymentWindowExpired ? 'primary' : 'outline-primary'"
          class="mb-75"
          block
          :disabled="isRequoting"
          @click="openRequoteConfirm('refresh')"
        >
          <b-spinner
            v-if="isRequoting && pendingRequoteMode === 'refresh'"
            small
            class="mr-50"
          />
          {{ $t('updateQuote') }}
        </b-button>
        <b-button
          variant="outline-primary"
          class="mb-75"
          block
          :disabled="isRequoting"
          @click="openRequoteConfirm('duplicate')"
        >
          <b-spinner
            v-if="isRequoting && pendingRequoteMode === 'duplicate'"
            small
            class="mr-50"
          />
          {{ $t('duplicateQuote') }}
        </b-button>
        <b-alert
          v-if="paymentWindowExpired"
          show
          variant="danger"
          class="mb-1"
        >
          <div class="font-weight-bolder mb-25">
            Cotización vencida
          </div>
          <div>
            Ya no está dentro del mes de su creación, por lo que no se puede pagar ni acreditar. Actualízala para reenviarla a subasta y reactivar el pago; el folio se mantiene.
          </div>
        </b-alert>
        <b-alert
          v-else-if="paymentDeadlineLabel && deadlineUpcoming"
          show
          :variant="deadlineVariant"
          class="mb-1"
        >
          <div>
            Se puede pagar hasta: <strong>{{ paymentDeadlineLabel }}</strong>
            <span v-if="deadlineUrgent"> — ¡queda poco tiempo!</span>
          </div>
        </b-alert>
        <b-button
          :to="{
            name: 'quoteInvoices',
            params: { quoteId: this.$route.params.quoteId },
          }"
          variant="outline-success"
          class="mb-75"
          block
        >
          {{ $t('invoice2') }}
        </b-button>
        <b-button
          v-if="quote.payment_attachment"
          :variant="
            quote.payment_status === 'payment_rejected'
              ? 'outline-danger'
              : 'outline-secondary'
          "
          class="mb-75"
          block
          target="_blank"
          :href="quote.payment_attachment"
        >
          {{ $t('invoice') }}
        </b-button>
        <!-- <b-button
          :to="{
            name: 'bankAccountData',
            params: { quoteId: quote.id },
          }"
          variant="outline-success"
          class="mb-75"
          block
        >
          {{ $t('payment') }}
        </b-button> -->
        <UploadPayment
          :quote="quote"
          @uploaded="handlePaymentStateChange"
          @removed="handlePaymentStateChange"
        />

        <b-button
          variant="outline-info"
          class="mb-75 mt-75"
          block
          :disabled="paymentWindowExpired || quote.is_paid || Number(remainingToPay) <= 0"
          @click="openWalletModal"
        >
          {{ $t('payWithWallet') }}
        </b-button>

        <confirmation-modal
          :id="'confirmWallet'"
          :variant="'info'"
          @confirm="payWithWalletConfirm"
        >
          <template v-slot:content>
            <div>
              <p class="mb-50">
                {{ $t('confirmWalletPayment') }}
              </p>
              <ul class="mb-0">
                <li>
                  {{ $t('quoteFolio') }}:
                  <strong>{{ quote.folio }}</strong>
                </li>

                <li>
                  {{ $t('subtotal') }}:
                  <strong>{{ formatMoney(subtotal) }}</strong>
                </li>
                <li>
                  {{ $t('shipping') }}:
                  <strong>{{ formatMoney(shippingTotal) }}</strong>
                </li>
                <li>
                  {{ $t('vat') }}:
                  <strong>{{ formatMoney(ivaTotal) }}</strong>
                </li>
                <li>
                  {{ $t('totalWithVAT') }}:
                  <strong>{{ formatMoney(total) }}</strong>
                </li>

                <li v-if="paymentsSum > 0">
                  {{ $t('alreadyPaid') }}:
                  <strong>{{ formatMoney(paymentsSum) }}</strong>
                </li>
                <li>
                  {{ $t('toCharge') }}:
                  <strong>{{ formatMoney(remainingToPay) }}</strong>
                </li>
                <li>
                  {{ $t('walletBalance') }}:
                  <strong>{{ formatMoney(walletBalance) }}</strong>
                </li>
              </ul>
            </div>
          </template>
        </confirmation-modal>

        <b-button
          variant="outline-secondary"
          class="mb-75 mt-75"
          block
          @click="printInvoice"
        >
          {{ $t('print') }}
        </b-button>
        <b-button
          variant="outline-dark"
          class="mb-75"
          block
          @click="openPrintPurchaseOrderModal"
        >
          {{ $t('purchaseOrderPrint.action') }}
        </b-button>
      </div>
      <span class="font-weight-bolder text-primary">{{ $t('supplier') }}</span>
      <div class="mt-1">
        <b-button
          :to="{
            name: 'purchaseOrders',
            params: { quoteId: $route.params.quoteId },
          }"
          variant="outline-success"
          class="mb-75"
          block
        >
          {{ $t('invoice2') }}
        </b-button>
        <b-button
          :to="{
            name: 'remissions',
            params: { quoteId: this.$route.params.quoteId },
          }"
          variant="outline-success"
          class="mb-75"
          block
        >
          {{ $t('remission') }}
        </b-button>
        <b-button
          variant="outline-success"
          class="mb-75"
          block
          @click="handleUploadRemission"
        >
          {{ $t('uploadRemission') }}
        </b-button>
        <!-- Update Delivery Status -->
        <b-button
          variant="outline-info"
          class="mb-75"
          block
          @click="showDeliveryModal = true"
        >
          {{ $t('DeliveryStatus') }}
        </b-button>

      </div>
      <!-- <b-button
        :to="{ name: 'clientQuote' }"
        variant="outline-secondary"
        class="mb-75"
        block
      >
        {{ $t('edit') }}
      </b-button> -->
    </b-card>
    <b-modal
      id="deliveryStatusModal"
      v-model="showDeliveryModal"
      title="Actualizar Estatus de Entrega"
      ok-title="Confirmar"
      cancel-title="Cancelar"
      @ok="confirmUpdateDeliveryStatus"
    >
      <b-form-group label="Selecciona el nuevo estatus de entrega">
        <b-form-select
          v-model="selectedDeliveryStatus"
          :options="deliveryStatusOptions"
        />
      </b-form-group>
    </b-modal>
    <b-modal
      id="selectPurchaseOrderModal"
      v-model="showModal"
      title="Seleccionar Orden de Compra"
      ok-title="Seleccionar"
      @ok="redirectToUploadRemission"
    >
      <b-form-group label="Orden de compra:">
        <b-form-select
          v-model="selectedPurchaseOrderId"
          :options="purchaseOrderOptions"
          required
        />
      </b-form-group>
    </b-modal>
    <b-modal
      id="printPurchaseOrderBySupplierPOAdmin"
      :title="$t('purchaseOrderPrint.selectSupplierTitle')"
      :ok-title="$t('purchaseOrderPrint.printButton')"
      :cancel-title="$t('cancel') || 'Cancel'"
      @ok="printPurchaseOrderBySupplier"
    >
      <div v-if="purchaseOrderSupplierSelectOptions.length === 0">
        {{ $t('purchaseOrderPrint.noSuppliers') }}
      </div>
      <div v-else>
        <p class="mb-1">
          {{ $t('purchaseOrderPrint.selectSupplierHelp') }}
        </p>
        <b-form-group :label="$t('purchaseOrderPrint.selectSupplierLabel')">
          <b-form-select
            v-model="selectedPurchaseOrderSupplierId"
            :options="purchaseOrderSupplierSelectOptions"
          />
        </b-form-group>
      </div>
    </b-modal>
    <confirmation-modal
      :id="'confirmRequote'"
      :variant="'warning'"
      @confirm="onRequoteQuote"
    >
      <template v-slot:content>
        <div>{{ requoteConfirmMessage }}</div>
      </template>
    </confirmation-modal>
  </div>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { mapActions } from 'vuex'
import { COMPANY_LEGAL_NAME } from '@/utils/company'
import UploadPayment from '../payments/UploadPayment.vue'
import ConfirmationModal from '@/components/ConfirmationModal.vue'
import resolveQuoteRoute from '@/utils/quoteRoute'
import { quoteHasPaymentActivity as hasPaymentActivity } from '@/utils/quotePurchase'

export default {
  components: { UploadPayment, ConfirmationModal },
  props: {
    quote: {
      type: Object,
      default() {
        return {}
      },
    },
    items: {
      type: Array,
      default() {
        return []
      },
    },
    project: {
      type: Object,
      default() {
        return {}
      },
    },
    supplierData: {
      type: Array,
      default() {
        return []
      },
    },
    refetch: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      showModal: false,
      selectedPurchaseOrderId: null,
      selectedPurchaseOrderSupplierId: null,
      selectedDeliveryStatus: null,
      showDeliveryModal: false,
      isRequoting: false,
      pendingRequoteMode: 'refresh',
    }
  },

  computed: {
    quoteHasPaymentActivity() {
      return hasPaymentActivity(this.quote)
    },
    requoteConfirmMessage() {
      return this.pendingRequoteMode === 'duplicate'
        ? 'Se creará una copia nueva de esta cotización. ¿Continuar?'
        : 'Se reenviará la cotización a subasta para buscar precios actualizados. El folio se mantiene y se reactiva la ventana de pago. ¿Continuar?'
    },
    paymentDeadlineAt() {
      return this.quote?.admin_payment_deadline_at || this.quote?.payment_deadline_at || null
    },
    paymentDeadlineLabel() {
      return this.formatDeadline(this.paymentDeadlineAt)
    },
    hoursUntilDeadline() {
      if (!this.paymentDeadlineAt) return null
      const d = new Date(this.paymentDeadlineAt)
      if (Number.isNaN(d.getTime())) return null
      return (d.getTime() - Date.now()) / (1000 * 60 * 60)
    },
    deadlineUpcoming() {
      return this.hoursUntilDeadline !== null && this.hoursUntilDeadline > 0
    },
    deadlineUrgent() {
      return this.deadlineUpcoming && this.hoursUntilDeadline <= 48
    },
    deadlineVariant() {
      return this.deadlineUrgent ? 'warning' : 'info'
    },
    purchaseOrders() {
      const po = this.$store.state.purchaseOrders.purchaseOrders
      return Array.isArray(po.data) ? po.data : []
    },
    purchaseOrderOptions() {
      return this.purchaseOrders.map(po => ({
        value: po.id,
        text: `${po.supplier_branch.name}`,
      }))
    },
    quoteItems() {
      if (Array.isArray(this.items) && this.items.length) return this.items
      if (Array.isArray(this.quote?.quote_items_attributes)) return this.quote.quote_items_attributes
      return []
    },
    purchaseOrderSuppliers() {
      const map = new Map()

      this.quoteItems
        .filter(item => item?.selected_supplier_branch_id && item?.active_status !== false)
        .forEach(item => {
          const supplierId = String(item.selected_supplier_branch_id)
          if (!map.has(supplierId)) {
            const name = this.getSupplierName(item)
            map.set(supplierId, {
              id: supplierId,
              name,
              sortKey: this.normalizeSupplierKey(name),
            })
          }
        })

      return Array.from(map.values()).sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    },
    purchaseOrderSupplierSelectOptions() {
      return [
        { value: null, text: this.$t('purchaseOrderPrint.selectSupplierPlaceholder') },
        ...this.purchaseOrderSuppliers.map(supplier => ({
          value: supplier.id,
          text: supplier.name,
        })),
      ]
    },
    selectedPurchaseOrderItems() {
      const selectedId = this.selectedPurchaseOrderSupplierId
      if (!selectedId) return []
      return this.quoteItems.filter(
        item => String(item?.selected_supplier_branch_id || '') === String(selectedId)
          && item?.active_status !== false,
      )
    },
    selectedPurchaseOrderSupplier() {
      return this.purchaseOrderSuppliers.find(
        supplier => String(supplier.id) === String(this.selectedPurchaseOrderSupplierId),
      ) || null
    },
    purchaseOrderShippingTotal() {
      if (this.quote?.without_freight) return 0

      const selectedSupplierId = this.selectedPurchaseOrderSupplierId
      const selectedSupplierName = this.normalizeSupplierKey(this.selectedPurchaseOrderSupplier?.name)
      const supplierBranchShippings = Array.isArray(this.quote?.supplier_branches_shippings)
        ? this.quote.supplier_branches_shippings
        : []

      if (!selectedSupplierId) return 0

      const freightOverride = (this.quote?.freight_overrides || {})[String(selectedSupplierId)]
      if (freightOverride !== undefined) return this.roundMoney(this.toNumber(freightOverride))

      const itemsShippingTotal = this.roundMoney(
        this.selectedPurchaseOrderItems.reduce(
          (sum, item) => sum + this.toNumber(
            item?.shipping_price
            ?? item?.shipping_total
            ?? item?.freight
            ?? 0,
          ),
          0,
        ),
      )

      if (itemsShippingTotal > 0) return itemsShippingTotal

      const matchingEntries = supplierBranchShippings.filter(entry => {
        const candidateIds = [
          entry?.id,
          entry?.supplier_branch_id,
          entry?.branch_id,
          entry?.selected_supplier_branch_id,
          entry?.supplier_id,
        ].filter(value => value !== null && value !== undefined && value !== '')

        if (candidateIds.some(id => String(id) === String(selectedSupplierId))) return true

        const entryName = this.normalizeSupplierKey(
          entry?.name
          || entry?.supplier_name
          || entry?.branch_name
          || entry?.supplier_branch_name
          || entry?.business_name,
        )

        return Boolean(selectedSupplierName && entryName && entryName === selectedSupplierName)
      })

      if (matchingEntries.length) {
        return this.roundMoney(
          matchingEntries.reduce(
            (sum, entry) => sum + this.getPurchaseOrderShippingAmount(entry),
            0,
          ),
        )
      }

      if (this.purchaseOrderSuppliers.length === 1) {
        return this.roundMoney(this.quote?.shipping_total || 0)
      }

      return 0
    },
    purchaseOrderSubtotal() {
      return this.roundMoney(
        this.selectedPurchaseOrderItems.reduce(
          (acc, item) => acc + this.getItemTotalWithoutCommission(item),
          0,
        ),
      )
    },
    purchaseOrderVatRate() {
      const raw = this.toNumber(this.quote?.iva_percentage ?? this.quote?.iva ?? 16)
      if (raw <= 0) return 0.16
      if (raw > 1) return raw / 100
      return raw
    },
    purchaseOrderTaxableAmount() {
      return this.roundMoney(this.purchaseOrderSubtotal + this.purchaseOrderShippingTotal)
    },
    purchaseOrderVatTotal() {
      return this.roundMoney(this.purchaseOrderTaxableAmount * this.purchaseOrderVatRate)
    },
    purchaseOrderGrandTotal() {
      return this.roundMoney(
        this.purchaseOrderSubtotal + this.purchaseOrderShippingTotal + this.purchaseOrderVatTotal,
      )
    },
    deliveryStatusOptions() {
      return [
        { value: 'in_warehouse', text: 'En bodega' },
        { value: 'on_the_way', text: 'En camino' },
        { value: 'delivered', text: 'Entregado' },
      ]
    },

    paymentWindowExpired() {
      return Boolean(this.quote?.admin_payment_window_expired ?? this.quote?.payment_window_expired)
    },

    payments() {
      return this.quote?.payments || this.$store.getters['quotes/quotePayments'] || []
    },
    paymentsSum() {
      return (this.payments || []).reduce((acc, p) => acc + Number(p.amount || 0), 0)
    },

    subtotal() {
      return Number(this.quote?.subtotal || 0)
    },
    shippingTotal() {
      return Number(this.quote?.shipping_total || 0)
    },
    ivaTotal() {
      return Number(this.quote?.iva_total || this.quote?.tax_total || 0)
    },
    total() {
      const t = Number(this.quote?.total)
      if (!Number.isNaN(t) && t > 0) return t
      return this.subtotal + this.shippingTotal + this.ivaTotal
    },
    remainingToPay() {
      const rest = Number(this.total) - Number(this.paymentsSum)
      return rest > 0 ? rest : 0
    },

    walletBalance() {
      const byCust = this.$store.state.wallet?.walletByCustomer?.data?.balance
      const me = this.$store.state.wallet?.walletMe?.data?.balance
      return Number(byCust ?? me ?? 0)
    },
  },

  created() {
    const quoteId = this.$route.params.quoteId || this.quote?.id
    if (quoteId) {
      this.fetchPurchaseOrders({ quoteId })
    } else {
      console.warn('No se pudo obtener quoteId')
    }

    this.selectedDeliveryStatus = this.quote?.delivery_status || 'in_warehouse'
  },

  methods: {
    ...mapActions('purchaseOrders', ['fetchPurchaseOrders']),
    ...mapActions('quotes', ['requoteQuote', 'fetchQuote', 'getQuotePayments']),

    printInvoice() {
      window.print()
    },
    openRequoteConfirm(mode) {
      this.pendingRequoteMode = mode
      this.$bvModal.show('confirmRequote')
    },
    formatDeadline(value) {
      if (!value) return ''
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      return new Intl.DateTimeFormat('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(d)
    },
    async handlePaymentStateChange() {
      const quoteId = this.$route.params.quoteId || this.quote?.id
      if (!quoteId) return

      try {
        const refreshedQuote = await this.fetchQuote(quoteId)
        await this.getQuotePayments({ quoteId })
        await this.fetchPurchaseOrders({ quoteId })

        if (typeof this.refetch === 'function') {
          await this.refetch()
        }

        const targetRoute = resolveQuoteRoute({
          quote: refreshedQuote,
          currentUser: this.$store.getters['auth/currentUser'] || {},
          routeParams: this.$route.params,
        })

        const targetFullPath = this.$router.resolve(targetRoute).route.fullPath

        if (targetFullPath && targetFullPath !== this.$route.fullPath) {
          window.location.replace(targetFullPath)
          return
        }

        this.$forceUpdate()
      } catch (error) {
        this.toast({
          title: error?.response?.data?.messages?.[0] || 'No se pudo refrescar la cotización después de actualizar el comprobante.',
          variant: 'danger',
        })
      }
    },
    openPrintPurchaseOrderModal() {
      if (!this.purchaseOrderSuppliers.length) {
        this.toast({ title: this.$t('purchaseOrderPrint.noSuppliers'), variant: 'warning' })
        return
      }

      if (!this.selectedPurchaseOrderSupplierId) {
        this.selectedPurchaseOrderSupplierId = this.purchaseOrderSuppliers[0].id
      }

      this.$bvModal.show('printPurchaseOrderBySupplierPOAdmin')
    },
    printPurchaseOrderBySupplier(modalEvent) {
      if (modalEvent && typeof modalEvent.preventDefault === 'function') {
        modalEvent.preventDefault()
      }

      if (!this.selectedPurchaseOrderSupplierId) {
        this.toast({ title: this.$t('purchaseOrderPrint.supplierRequired'), variant: 'warning' })
        return
      }

      const rows = this.selectedPurchaseOrderItems.map(item => {
        const unitType = item?.product_catalog?.unit_type || item?.unit_type || 'noUnit'
        const unitsFilter = this.$store.getters['app/unitsFilter']
        const unitKey = typeof unitsFilter === 'function' ? unitsFilter(unitType) : unitType

        return {
          quantity: this.toNumber(item?.quantity),
          unit: this.$tc(unitKey, this.toNumber(item?.quantity) || 1),
          description: item?.product_catalog?.name || item?.name || this.$t('product'),
          unitPrice: this.getItemUnitPriceWithoutCommission(item),
          amount: this.getItemTotalWithoutCommission(item),
        }
      })

      if (!rows.length) {
        this.toast({ title: this.$t('purchaseOrderPrint.emptyItems'), variant: 'warning' })
        return
      }

      const printHtml = this.buildPurchaseOrderPrintHtml({
        supplierName: this.selectedPurchaseOrderSupplier?.name || this.$t('supplier'),
        projectName: this.project?.name || this.$t('project'),
        projectAddress: this.normalizeText(this.project?.address?.formatted_address),
        projectMapsUrl: this.normalizeText(this.project?.google_maps_url),
        observations: this.project?.observations || this.$t('noObservations'),
        rows,
        subtotalAmount: this.purchaseOrderSubtotal,
        shippingAmount: this.purchaseOrderShippingTotal,
        vatAmount: this.purchaseOrderVatTotal,
        totalAmount: this.purchaseOrderGrandTotal,
        dateText: this.formatDate(this.quote?.published_at || new Date()),
        ocFolio: `OC-${this.quote?.folio || this.$route.params.quoteId || ''}`,
        paymentCondition: this.$t('credit'),
        buyerName: this.$t('purchaseOrderPrint.defaultBuyer'),
        directorName: this.$t('purchaseOrderPrint.defaultDirector'),
        directorRole: this.$t('purchaseOrderPrint.directorRole'),
      })

      const opened = this.openPrintWindow(printHtml)
      if (opened) this.$bvModal.hide('printPurchaseOrderBySupplierPOAdmin')
    },

    async onRequoteQuote() {
      try {
        this.isRequoting = true
        const sourceQuoteId = this.$route.params.quoteId || this.quote?.id

        if (!sourceQuoteId) {
          throw new Error(this.$t('quoteNotFoundForRequote'))
        }

        const newQuote = await this.requoteQuote({ quoteId: sourceQuoteId, mode: this.pendingRequoteMode })

        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('quoteRequoted'),
            icon: 'CopyIcon',
            variant: 'success',
          },
        })

        if (String(newQuote.id) === String(sourceQuoteId)) {
          const targetRoute = resolveQuoteRoute({
            quote: newQuote,
            currentUser: this.$store.getters['auth/currentUser'] || {},
            routeParams: this.$route.params,
          })
          if (targetRoute) {
            await this.$router.push(targetRoute).catch(() => {})
          } else {
            await this.fetchQuote(sourceQuoteId)
          }
          return
        }

        this.$router.push({
          name: 'addProductsToQuote',
          params: { quoteId: newQuote.id },
        })
      } catch (err) {
        const msg = err?.data?.messages?.[0] || err?.statusText || err?.message || this.$t('quoteRequoteFailed')
        this.$toast({
          component: ToastificationContent,
          props: {
            title: msg,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.isRequoting = false
      }
    },

    handleUploadRemission() {
      if (this.purchaseOrders.length === 1) {
        this.$router.push({
          name: 'uploadRemission',
          params: { purchaseOrderId: this.purchaseOrders[0].id },
        })
      } else if (this.purchaseOrders.length > 1) {
        this.showModal = true
      } else {
        console.log('❌ No hay órdenes de compra')
      }
    },

    redirectToUploadRemission() {
      if (this.selectedPurchaseOrderId) {
        this.$router.push({
          name: 'uploadRemission',
          params: { purchaseOrderId: this.selectedPurchaseOrderId },
        })
      }
    },

    confirmUpdateDeliveryStatus() {
      this.$store
        .dispatch('quotes/updateDeliveryStatus', {
          quoteId: this.$route.params.quoteId,
          delivery_status: this.selectedDeliveryStatus,
        })
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Estatus de entrega actualizado',
              icon: 'TruckIcon',
              variant: 'success',
            },
          })
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error al actualizar el estatus',
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          })
        })
    },

    formatMoney(n) {
      try {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })
          .format(Number(n || 0))
      } catch (e) {
        return `$${Number(n || 0).toFixed(2)}`
      }
    },
    formatMoneyNoCurrency(n) {
      try {
        return new Intl.NumberFormat('es-MX', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(n || 0))
      } catch (e) {
        return Number(n || 0).toFixed(2)
      }
    },
    formatDate(value) {
      const d = value ? new Date(value) : new Date()
      if (Number.isNaN(d.getTime())) return ''
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}/${month}/${year}`
    },
    toNumber(value) {
      const n = Number(value)
      return Number.isFinite(n) ? n : 0
    },
    roundMoney(value) {
      return Math.round(this.toNumber(value) * 100) / 100
    },
    normalizeText(value) {
      return String(value || '').trim()
    },
    normalizeSupplierKey(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
    },
    getSupplierName(item) {
      // Este panel es solo del admin: si la cotizacion esta cerrada el nombre visible viene
      // enmascarado, pero la OC impresa lleva el proveedor real.
      const direct = this.normalizeText(
        item?.unmasked_supplier_branch_name
        || item?.selected_supplier_branch_name
        || item?.selected_supplier_name
        || item?.supplier_name
        || item?.branch_name
        || item?.business_name
        || item?.supplier,
      )
      if (direct) return direct

      const branchId = item?.selected_supplier_branch_id
      if (branchId && Array.isArray(this.supplierData)) {
        const found = this.supplierData.find(s => String(s?.id) === String(branchId))
        const fallback = this.normalizeText(
          found?.name
          || found?.branch_name
          || found?.supplier_name
          || found?.business_name,
        )
        if (fallback) return fallback
      }

      return this.$t('supplier')
    },
    getItemTotalWithoutCommission(item) {
      if (item?.total_without_commission !== null && item?.total_without_commission !== undefined) {
        return this.roundMoney(item.total_without_commission)
      }
      return this.roundMoney(this.toNumber(item?.quantity) * this.toNumber(item?.unit_price))
    },
    getItemUnitPriceWithoutCommission(item) {
      if (item?.unit_price !== null && item?.unit_price !== undefined) {
        return this.roundMoney(item.unit_price)
      }
      const qty = this.toNumber(item?.quantity)
      const total = this.getItemTotalWithoutCommission(item)
      if (qty <= 0) return 0
      return this.roundMoney(total / qty)
    },
    getPurchaseOrderShippingAmount(supplierShippingEntry) {
      const directAmount = [
        supplierShippingEntry?.shipping_total,
        supplierShippingEntry?.shipping_price,
        supplierShippingEntry?.price,
        supplierShippingEntry?.amount,
        supplierShippingEntry?.cost,
      ].find(value => value !== null && value !== undefined && value !== '')

      if (directAmount !== undefined) return this.roundMoney(directAmount)

      const shippings = Array.isArray(supplierShippingEntry?.shippings)
        ? supplierShippingEntry.shippings
        : []

      const selectedShipping = shippings.find(shipping => shipping?.selected)
        || shippings.find(shipping => shipping?.is_selected)
        || shippings.find(shipping => shipping?.active)
        || (shippings.length === 1 ? shippings[0] : null)
        || null

      if (!selectedShipping) return 0

      return this.roundMoney(
        selectedShipping?.price
        ?? selectedShipping?.amount
        ?? selectedShipping?.cost
        ?? selectedShipping?.shipping_total
        ?? 0,
      )
    },
    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    buildPurchaseOrderPrintHtml({
      supplierName,
      projectName,
      projectAddress,
      projectMapsUrl,
      observations,
      rows,
      subtotalAmount,
      shippingAmount,
      vatAmount,
      totalAmount,
      dateText,
      ocFolio,
      paymentCondition,
      buyerName,
      directorName,
      directorRole,
    }) {
      const maxRows = 12
      const emptyRows = Math.max(0, maxRows - rows.length)
      // Absoluta a proposito: el documento se escribe en una ventana about:blank, donde una
      // ruta relativa no resuelve. Vive en public/ y no en assets/ para que un require() a
      // un archivo que falte no tumbe la compilacion; si no esta, el onerror quita la imagen.
      const signatureUrl = `${window.location.origin}${process.env.BASE_URL}img/orden-de-compra/firma.png`
      const tableRows = rows.map(row => `
        <tr>
          <td class="ta-c">${this.escapeHtml(row.quantity)}</td>
          <td class="ta-c">${this.escapeHtml(row.unit)}</td>
          <td>${this.escapeHtml(row.description)}</td>
          <td class="ta-r">$ ${this.escapeHtml(this.formatMoneyNoCurrency(row.unitPrice))}</td>
          <td class="ta-r">$ ${this.escapeHtml(this.formatMoneyNoCurrency(row.amount))}</td>
        </tr>
      `).join('')
      const fillerRows = Array.from({ length: emptyRows }, () => `
        <tr class="filler">
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
        </tr>
      `).join('')

      return `
        <!doctype html>
        <html lang="es">
          <head>
            <meta charset="utf-8" />
            <title>${this.escapeHtml(this.$t('purchaseOrderPrint.orderTitle'))}</title>
            <style>
              * { box-sizing: border-box; }
              body { margin: 0; font-family: Arial, sans-serif; color: #111827; background: #fff; }
              .sheet { width: 210mm; margin: 0 auto; padding: 10mm 12mm; }
              .title { text-align: center; font-size: 34px; font-weight: 700; margin: 0 0 12px; letter-spacing: 0.3px; }
              .grid { border: 1px solid #9ca3af; border-bottom: 0; }
              .row { display: grid; grid-template-columns: 180px 1fr 130px 180px; border-bottom: 1px solid #9ca3af; min-height: 34px; }
              .cell { padding: 7px 8px; border-right: 1px solid #9ca3af; display: flex; align-items: center; font-size: 14px; }
              .cell:last-child { border-right: 0; }
              .label { background: #f3f4f6; font-weight: 700; }
              .value { font-weight: 600; }
              .wrap { word-break: break-word; white-space: pre-wrap; }
              .obs { min-height: 58px; white-space: pre-wrap; align-items: flex-start; padding-top: 10px; }
              .link { color: #2563eb; text-decoration: underline; }
              .sub-title { margin: 10px 0 6px; font-size: 15px; font-weight: 700; }
              table { width: 100%; border-collapse: collapse; table-layout: fixed; }
              th, td { border: 1px solid #9ca3af; padding: 6px 7px; font-size: 14px; }
              th { background: #f3f4f6; text-transform: uppercase; font-size: 13px; font-weight: 700; }
              .ta-r { text-align: right; }
              .ta-c { text-align: center; }
              .totals-wrap { display: flex; justify-content: flex-end; margin-top: 8px; }
              .totals { width: 320px; }
              .totals .line { display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px solid #d1d5db; font-size: 16px; }
              .totals .line strong { font-size: 18px; }
              .signature { margin-top: 28px; text-align: center; break-inside: avoid; page-break-inside: avoid; }
              .signature-image { display: block; max-height: 90px; max-width: 320px; margin: 0 auto -14px; }
              .signature-line { margin: 0 auto 8px; width: 360px; border-top: 1px solid #111827; }
              .signature-name { font-size: 16px; font-weight: 700; }
              .signature-role { font-size: 13px; color: #4b5563; }
              @page { size: auto; margin: 0; }
            </style>
          </head>
          <body>
            <div class="sheet">
              <h1 class="title">${this.escapeHtml(this.$t('purchaseOrderPrint.orderTitle'))}</h1>
              <div class="grid">
                <div class="row">
                  <div class="cell label">${this.escapeHtml(this.$t('purchaseOrderPrint.orderLabel'))}</div>
                  <div class="cell value">${this.escapeHtml(ocFolio)}</div>
                  <div class="cell label">${this.escapeHtml(this.$t('date'))}</div>
                  <div class="cell value">${this.escapeHtml(dateText)}</div>
                </div>
                <div class="row">
                  <div class="cell label">${this.escapeHtml(this.$t('project'))}</div>
                  <div class="cell value">${this.escapeHtml(projectName)}</div>
                  <div class="cell label">${this.escapeHtml(this.$t('purchaseOrderPrint.buyer'))}</div>
                  <div class="cell value">${this.escapeHtml(buyerName)}</div>
                </div>
                <div class="row">
                  <div class="cell label">${this.escapeHtml(this.$t('address'))}</div>
                  <div class="cell value wrap" style="grid-column: span 3;">${this.escapeHtml(projectAddress || '—')}</div>
                </div>
                <div class="row">
                  <div class="cell label">Google Maps</div>
                  <div class="cell value wrap" style="grid-column: span 3;">
                    ${projectMapsUrl
    ? `<a class="link" href="${this.escapeHtml(projectMapsUrl)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(projectMapsUrl)}</a>`
    : '—'}
                  </div>
                </div>
                <div class="row">
                  <div class="cell label">${this.escapeHtml(this.$t('supplier'))}</div>
                  <div class="cell value">${this.escapeHtml(supplierName)}</div>
                  <div class="cell label">${this.escapeHtml(this.$t('purchaseOrderPrint.paymentConditions'))}</div>
                  <div class="cell value">${this.escapeHtml(paymentCondition)}</div>
                </div>
                <div class="row">
                  <div class="cell label">${this.escapeHtml(this.$t('observations'))}</div>
                  <div class="cell value obs" style="grid-column: span 3;">${this.escapeHtml(observations || '—')}</div>
                </div>
                <div class="row">
                  <div class="cell label">${this.escapeHtml(this.$t('purchaseOrderPrint.issuer'))}</div>
                  <div class="cell value" style="grid-column: span 3;">${this.escapeHtml(COMPANY_LEGAL_NAME)}</div>
                </div>
              </div>

              <p class="sub-title">${this.escapeHtml(this.$t('purchaseOrderPrint.materialsBreakdown'))}</p>
              <table>
                <thead>
                  <tr>
                    <th style="width: 110px;">${this.escapeHtml(this.$t('quantity'))}</th>
                    <th style="width: 90px;">${this.escapeHtml(this.$t('unit'))}</th>
                    <th>${this.escapeHtml(this.$t('description'))}</th>
                    <th style="width: 150px;">${this.escapeHtml(this.$t('unitPrice'))}</th>
                    <th style="width: 170px;">${this.escapeHtml(this.$t('total'))}</th>
                  </tr>
                </thead>
                <tbody>
                  ${tableRows}
                  ${fillerRows}
                </tbody>
              </table>

              <div class="totals-wrap">
                <div class="totals">
                  <div class="line"><span>${this.escapeHtml(this.$t('subtotal'))}</span><span><strong>$ ${this.escapeHtml(this.formatMoneyNoCurrency(subtotalAmount))}</strong></span></div>
                  <div class="line"><span>${this.escapeHtml(this.$t('shipping') || 'Flete')}</span><span><strong>$ ${this.escapeHtml(this.formatMoneyNoCurrency(shippingAmount))}</strong></span></div>
                  <div class="line"><span>${this.escapeHtml(this.$t('vat'))}</span><span><strong>$ ${this.escapeHtml(this.formatMoneyNoCurrency(vatAmount))}</strong></span></div>
                  <div class="line"><span>${this.escapeHtml(this.$t('total'))}</span><span><strong>$ ${this.escapeHtml(this.formatMoneyNoCurrency(totalAmount))}</strong></span></div>
                </div>
              </div>

              <div class="signature">
                <img class="signature-image" src="${this.escapeHtml(signatureUrl)}" alt="" onerror="this.remove()" />
                <div class="signature-line"></div>
                <div class="signature-name">${this.escapeHtml(directorName)}</div>
                <div class="signature-role">${this.escapeHtml(directorRole)}</div>
              </div>
            </div>
          </body>
        </html>
      `
    },
    openPrintWindow(html) {
      const printWindow = window.open('', '_blank', 'width=1100,height=900')
      if (!printWindow) {
        this.toast({ title: this.$t('purchaseOrderPrint.popupBlocked'), variant: 'danger' })
        return false
      }

      printWindow.document.open()
      printWindow.document.write(html)
      printWindow.document.close()
      printWindow.focus()

      // La firma es una imagen: si se imprime antes de que cargue, sale en blanco. Se espera
      // a todas las imagenes del documento (cargadas o fallidas) y luego el margen de render.
      const images = Array.from(printWindow.document.images)
      const settled = images.map(img => (img.complete
        ? Promise.resolve()
        : new Promise(resolve => {
          img.onload = resolve
          img.onerror = resolve
        })))

      Promise.all(settled).then(() => {
        this.fitPrintSheetToOnePage(printWindow.document)
        setTimeout(() => {
          printWindow.print()
          printWindow.close()
        }, 280)
      })

      return true
    },

    fitPrintSheetToOnePage(doc) {
      const sheet = doc.querySelector('.sheet')
      if (!sheet) return

      const mmToPx = 96 / 25.4
      const pageHeightPx = 279.4 * mmToPx
      const paddingBottomPx = 10 * mmToPx
      const signature = doc.querySelector('.signature')
      const fillers = Array.from(doc.querySelectorAll('tr.filler')).reverse()
      const contentBottom = () => (signature || sheet.lastElementChild).getBoundingClientRect().bottom
        - sheet.getBoundingClientRect().top + paddingBottomPx
      const overflows = () => contentBottom() > pageHeightPx - 8

      while (overflows() && fillers.length) {
        fillers.shift().remove()
      }
    },

    toast({ title, variant = 'success' }) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: 'CreditCardIcon', variant },
      })
    },
    getErrorMessage(error, fallback = 'Ocurrió un error.') {
      const payload = error?.response?.data || error?.data || {}
      if (Array.isArray(payload.messages) && payload.messages.length > 0) return payload.messages[0]
      if (typeof payload.error === 'string') return payload.error
      if (typeof error?.message === 'string') return error.message
      return fallback
    },
    async ensureTaxInformationForPayment() {
      const quoteId = this.$route.params.quoteId || this.quote?.id
      const storeQuote = this.$store.state.quotes?.quote || {}
      const isSameQuoteInStore = quoteId && storeQuote?.id
        && String(storeQuote.id) === String(quoteId)

      if (
        this.quote?.tax_information_id
        || this.quote?.taxable_id
        || this.quote?.tax_information?.id
        || (isSameQuoteInStore && (storeQuote?.tax_information_id || storeQuote?.taxable_id))
      ) return true

      if (!quoteId) {
        this.toast({ title: 'No se encontró la cotización.', variant: 'danger' })
        return false
      }

      try {
        const optionsResponse = await this.$store.dispatch('quotes/fetchQuoteTaxInformationOptions', { quoteId })
        const options = optionsResponse?.data || []
        const selectedId = optionsResponse?.selected_tax_information_id
          || optionsResponse?.project_default_tax_information_id
          || optionsResponse?.customer_default_tax_information_id
          || options?.[0]?.id
          || null

        if (!selectedId) {
          this.toast({
            title: 'Debes registrar una razón social de facturación antes de pagar la cotización.',
            variant: 'warning',
          })
          const customerId = optionsResponse?.customer_id
            || this.quote?.customer_id
            || this.quote?.customer?.id
            || this.quote?.customer_attributes?.id
          if (customerId) {
            this.$router.push({
              name: 'new-billing',
              params: { customerId },
            })
          }
          return false
        }

        await this.$store.dispatch('quotes/setQuoteTaxInformation', {
          quoteId,
          taxInformationId: selectedId,
        })
        await this.$store.dispatch('quotes/fetchQuote', quoteId)
        return true
      } catch (error) {
        this.toast({ title: this.getErrorMessage(error, 'No fue posible asignar la razón social.'), variant: 'danger' })
        return false
      }
    },
    async loadWalletForQuote() {
      const quoteCust = this.quote?.customer_id
        || this.quote?.customer?.id
        || this.quote?.customer_attributes?.id
      if (!quoteCust) throw new Error('La cotización no trae customer_id')

      await this.$store.dispatch('wallet/fetchWalletByCustomer', { customerId: quoteCust })
    },

    async openWalletModal() {
      try {
        const canContinue = await this.ensureTaxInformationForPayment()
        if (!canContinue) return
        await this.loadWalletForQuote()
        this.$bvModal.show('confirmWallet')
      } catch (err) {
        this.toast({ title: this.getErrorMessage(err, 'No se pudo cargar el monedero'), variant: 'danger' })
      }
    },

    async payWithWalletConfirm() {
      try {
        const wasCreditApproved = this.quote?.payment_status === 'credit_approved'

        if (Number(this.walletBalance) < Number(this.remainingToPay)) {
          this.toast({ title: (this.$t('insufficientWallet') || 'Saldo insuficiente'), variant: 'warning' })
          return
        }

        const quoteId = this.$route.params.quoteId || this.quote?.id
        const amount = this.remainingToPay
        const idempotencyKey = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now())

        await this.$store.dispatch('quotes/payWithWallet', { quoteId, amount, idempotency_key: idempotencyKey })

        this.toast({ title: (this.$t('walletPaymentOk') || 'Pago con monedero realizado') })
        this.$bvModal.hide('confirmWallet')

        if (wasCreditApproved) {
          const projectId = this.quote?.project_id
          if (!projectId) {
            this.toast({ title: 'No se encontró projectId para redirigir a Mis compras', variant: 'warning' })
            return
          }
          this.$router.replace({ name: 'approvedQuotes', params: { projectId } })
          return
        }
      } catch (err) {
        const code = err?.response?.data?.code
        const msg = err?.response?.data?.messages?.[0] || err?.response?.data?.error || err?.message

        if (code === 'QUOTE_PAYMENT_WINDOW_EXPIRED') {
          this.toast({ title: msg || 'Cotización vencida', variant: 'warning' })
          return
        }
        this.toast({ title: msg || 'Error al pagar con monedero', variant: 'danger' })
      }
    },

  },
}
</script>

<style></style>
